import React, { CSSProperties } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Step, StepLabel, Stepper, Grid } from '@mui/material';
import { MAIN_THEME } from '@styles';
import EnhancedButton, { EnhancedButtonStatus } from '../_old/EnhancedButton';
import { CDN_URL } from '@utils';

export interface IEnhancedStepperProps {
  activeStep: string;
  steps: string[];
  className?: string;
  style?: CSSProperties;
  showStepperButton?: boolean;
  buttonTitle?: string;
  buttonOnClick?: () => void;
  buttonColor?: string;
  buttonState?: EnhancedButtonStatus;
  buttonDisabled?: boolean;
}

const useStyles = makeStyles()(() => ({
  stepper: {
    backgroundColor: 'transparent',
    padding: 0,
    width: '100%',
  },
  step: {
    '& .MuiStepIcon-root': {
      height: 26,
      width: 26,
      color: MAIN_THEME.color.white86,
    },
    // Update the color to gray
    color: MAIN_THEME.color.black47,
    '& .MuiStepIcon-active': {
      color: MAIN_THEME.color.black,
      '& .MuiStepIcon-text': {
        fontSize: MAIN_THEME.fontSize.xs,
        lineHeight: '12px',
        fill: 'white',
      },
    },
    '& .MuiStepConnector-alternativeLabel': {
      left: 'calc(-50% + 35px)',
      right: 'calc(50% + 35px)',
    },
  },
  stepLabel: {
    '& .MuiStepLabel-label': {
      fontSize: MAIN_THEME.fontSize.xs,
      lineHeight: '13px',
      fontFamily: MAIN_THEME.font.regular,
      margin: '1.5em 0 0 0',
    },
  },
}));

const EnhancedStepperSpecificDesign: React.FC<IEnhancedStepperProps> = ({
  steps,
  activeStep,
  className,
  style,
  showStepperButton = false,
  buttonTitle = 'Modify',
  buttonOnClick,
  buttonColor = MAIN_THEME.color.black,
  buttonState = undefined,
  buttonDisabled = false,
}) => {
  const { classes } = useStyles();

  // Create a set of completed steps based on the active step
  const completedSteps = steps.slice(0, steps.indexOf(activeStep));

  return (
    <Grid container alignItems="center" spacing={showStepperButton ? 2 : 2}>
      <Grid item xs={showStepperButton ? 10 : 10}>
        <Stepper
          alternativeLabel
          classes={{ root: classes.stepper }}
          className={className}
          style={style}
        >
          {steps.map((label) => {
            const isActive = activeStep === label;
            const isCompleted = completedSteps.includes(label);
            const labelColor = isCompleted
              ? MAIN_THEME.color.black
              : isActive
                ? MAIN_THEME.color.black
                : MAIN_THEME.color.white86;

            return (
              <Step key={label} classes={{ root: classes.step }}>
                <StepLabel
                  classes={{ root: classes.stepLabel }}
                  StepIconComponent={
                    activeStep.includes(label)
                      ? () => (
                          <img
                            src={CDN_URL + '/icons/active-step.svg'}
                            alt="Active"
                            width={33}
                            height={33}
                            style={{ margin: '-3px 0px' }}
                          />
                        )
                      : undefined
                  }
                  StepIconProps={{
                    completed: completedSteps.includes(label) ? false : false,
                  }}
                >
                  <span style={{ color: labelColor }}>{label}</span>
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Grid>
      {showStepperButton && (
        <Grid item xs={2} justifyContent="flex-end">
          <EnhancedButton
            type="button"
            backgroundColor={buttonColor}
            color={MAIN_THEME.color.white}
            onClick={buttonOnClick}
            style={{ marginTop: '-15px' }}
            state={buttonState}
            disabled={buttonDisabled}
          >
            {buttonTitle}
          </EnhancedButton>
        </Grid>
      )}
    </Grid>
  );
};

export default EnhancedStepperSpecificDesign;
